import React, { useState, useEffect } from 'react';
import { Plus, Edit, Search, Download, X, Eye, EyeOff } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api/v1';

const extensiones = ['LP', 'CB', 'SC', 'OR', 'PT', 'CH', 'TJ', 'BE', 'PD'];
const nivelesFormacion = ['Licenciatura', 'Maestría', 'Doctorado', 'Técnico Superior', 'Diplomado'];
const discapacidades = ['Ninguna', 'Visual', 'Auditiva', 'Motriz', 'Intelectual', 'Otra'];

export default function JefaturaDocentesAltas() {
    const [docentes, setDocentes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    const [selectedDocente, setSelectedDocente] = useState(null);
    const [editingId, setEditingId] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [errors, setErrors] = useState({});
    const [showPassword, setShowPassword] = useState(false);

    const initialFormData = {
        nombres: '', primer_apellido: '', segundo_apellido: '',
        usuario: '', password: '',
        cedula: '', extension: '', complemento: '',
        fecha_nacimiento: '', sexo: '',
        telefono: '', email: '', discapacidad: '',
        especialidad: '', nivel_formacion: '', descripcion: '',
        otra_formacion: '', activo: true,
        fecha_alta: '', fecha_baja: '',
        cite_memorandum_alta: '', cite_memorandum_baja: '',
        observaciones: ''
    };

    const [formData, setFormData] = useState(initialFormData);

    useEffect(() => {
        loadDocentes();
    }, []);

    const loadDocentes = async () => {
        try {
            setLoading(true);
            const response = await fetch(`${API_URL}/jefatura/docentes?includeInactive=true`);
            const data = await response.json();
            setDocentes(data.docentes || []);
        } catch (error) {
            console.error('Error:', error);
            setDocentes([]);
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        let validatedValue = value;

        // Solo letras, espacios y acentos para nombres/apellidos
        if (['nombres', 'primer_apellido', 'segundo_apellido'].includes(name)) {
            validatedValue = value.replace(/[^a-zA-ZáéíóúÁÉÍÓÚñÑüÜ\s]/g, '');
        }
        // Usuario
        if (name === 'usuario') {
            validatedValue = value.replace(/\s/g, ''); // Sin espacios
        }
        // Solo números para teléfono
        if (name === 'telefono') {
            validatedValue = value.replace(/[^0-9]/g, '');
        }
        // Solo números para cédula
        if (name === 'cedula') {
            validatedValue = value.replace(/[^0-9]/g, '');
        }
        // Solo alfanumérico y guiones para complemento
        if (name === 'complemento') {
            validatedValue = value.replace(/[^a-zA-Z0-9-]/g, '');
        }
        // Email: no filtrar al escribir, solo validar al enviar

        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : validatedValue
        }));
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    };

    const validateForm = () => {
        const newErrors = {};

        // Nombres (requerido, solo letras)
        if (!formData.nombres.trim()) {
            newErrors.nombres = 'Nombres es requerido';
        } else if (formData.nombres.trim().length < 2) {
            newErrors.nombres = 'Nombres debe tener al menos 2 caracteres';
        }

        // Primer apellido (requerido)
        if (!formData.primer_apellido.trim()) {
            newErrors.primer_apellido = 'Primer apellido es requerido';
        }

        // Usuario (requerido al crear)
        if (!editingId && !formData.usuario.trim()) {
            newErrors.usuario = 'Usuario es requerido';
        }

        // Contraseña (requerida al crear)
        if (!editingId && (!formData.password || formData.password.length < 8)) {
            newErrors.password = 'Contraseña debe tener al menos 8 caracteres';
        }
        if (editingId && formData.password && formData.password.length < 8) {
            newErrors.password = 'Contraseña debe tener al menos 8 caracteres';
        }

        // Cédula (requerida, solo números, 5-10 dígitos)
        if (!formData.cedula.trim()) {
            newErrors.cedula = 'Cédula es requerida';
        } else if (!/^\d{5,10}$/.test(formData.cedula.trim())) {
            newErrors.cedula = 'Cédula debe tener entre 5 y 10 dígitos';
        }

        // Extensión (requerida)
        if (!formData.extension) {
            newErrors.extension = 'Extensión es requerida';
        }

        // Fecha nacimiento (requerida)
        if (!formData.fecha_nacimiento) {
            newErrors.fecha_nacimiento = 'Fecha de nacimiento es requerida';
        }

        // Sexo (requerido)
        if (!formData.sexo) {
            newErrors.sexo = 'Sexo es requerido';
        }

        // Teléfono (requerido, 7-8 dígitos)
        if (!formData.telefono.trim()) {
            newErrors.telefono = 'Teléfono es requerido';
        } else if (!/^\d{7,8}$/.test(formData.telefono.trim())) {
            newErrors.telefono = 'Teléfono debe tener 7 u 8 dígitos';
        }

        // Email (requerido, formato válido)
        if (!formData.email.trim()) {
            newErrors.email = 'Correo electrónico es requerido';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
            newErrors.email = 'Formato de correo inválido';
        }

        // Especialidad (requerida)
        if (!formData.especialidad.trim()) {
            newErrors.especialidad = 'Especialidad es requerida';
        }

        // Nivel de formación (requerido)
        if (!formData.nivel_formacion) {
            newErrors.nivel_formacion = 'Nivel de formación es requerido';
        }

        setErrors(newErrors);
        if (Object.keys(newErrors).length > 0) {
            // Scroll al primer error
            const firstErrorField = document.querySelector('.border-red-500');
            if (firstErrorField) firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;

        try {
            const url = editingId
                ? `${API_URL}/jefatura/docentes/${editingId}`
                : `${API_URL}/jefatura/docentes`;

            const response = await fetch(url, {
                method: editingId ? 'PUT' : 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(formData)
            });

            if (response.ok) {
                setShowModal(false);
                setEditingId(null);
                setFormData(initialFormData);
                loadDocentes();
            } else {
                const error = await response.json();
                alert(error.error || 'Error al guardar docente');
            }
        } catch (error) {
            alert('Error al guardar docente');
        }
    };

    const handleEdit = (docente) => {
        setEditingId(docente.id_docente);
        setFormData({
            nombres: docente.nombres || '',
            primer_apellido: docente.apellidos || '',
            segundo_apellido: docente.segundo_apellido || '',
            usuario: docente.usuario || '',
            password: '',
            cedula: docente.cedula || '',
            extension: docente.extension || '',
            complemento: docente.complemento || '',
            fecha_nacimiento: docente.fecha_nacimiento ? docente.fecha_nacimiento.split('T')[0] : '',
            sexo: docente.sexo || '',
            telefono: docente.telefono || docente.celular || '',
            email: docente.email || '',
            discapacidad: docente.discapacidad || '',
            especialidad: docente.especialidad || '',
            nivel_formacion: docente.nivel_formacion || '',
            descripcion: docente.descripcion || '',
            otra_formacion: docente.otra_formacion || '',
            activo: docente.activo !== false,
            fecha_alta: docente.fecha_alta ? docente.fecha_alta.split('T')[0] : '',
            fecha_baja: docente.fecha_baja ? docente.fecha_baja.split('T')[0] : '',
            cite_memorandum_alta: docente.cite_memorandum_alta || '',
            cite_memorandum_baja: docente.cite_memorandum_baja || '',
            observaciones: docente.observaciones || ''
        });
        setShowModal(true);
    };

    const handleView = (docente) => {
        setSelectedDocente(docente);
        setShowViewModal(true);
    };

    const handleNew = () => {
        setEditingId(null);
        setFormData(initialFormData);
        setErrors({});
        setShowPassword(false);
        setShowModal(true);
    };

    const exportToExcel = () => {
        const headers = ['No', 'Nombres', 'Apellidos', 'Cédula', 'Extensión', 'Complemento', 'Fecha Nacimiento', 'Sexo',
            'Teléfono', 'Correo', 'Discapacidad', 'Especialidad', 'Nivel Formación', 'Descripción',
            'Otra Formación', 'Activo', 'Fecha Alta', 'Fecha Baja', 'CITE Alta', 'CITE Baja', 'Observaciones'];
        const rows = filteredDocentes.map((d, i) => [
            i + 1, d.nombres || '', `${d.apellidos || ''} ${d.segundo_apellido || ''}`.trim(),
            d.cedula || '', d.extension || '', d.complemento || '',
            d.fecha_nacimiento ? new Date(d.fecha_nacimiento).toLocaleDateString() : '',
            d.sexo || '', d.telefono || d.celular || '', d.email || '', d.discapacidad || '',
            d.especialidad || '', d.nivel_formacion || '', d.descripcion || '', d.otra_formacion || '',
            d.activo ? 'Sí' : 'No',
            d.fecha_alta ? new Date(d.fecha_alta).toLocaleDateString() : '',
            d.fecha_baja ? new Date(d.fecha_baja).toLocaleDateString() : '',
            d.cite_memorandum_alta || '', d.cite_memorandum_baja || '', d.observaciones || ''
        ]);

        const csvContent = [headers, ...rows].map(row =>
            row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')
        ).join('\n');

        const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `docentes_altas_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const filteredDocentes = docentes.filter(d =>
        searchTerm === '' ||
        d.nombres?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        d.apellidos?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        d.cedula?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        d.especialidad?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (loading) return <div className="p-6 text-center">Cargando...</div>;

    return (
        <div className="p-4 md:p-6">
            <div className="mb-6">
                <h1 className="text-xl font-bold text-gray-800">Jefatura de Carrera &gt; Docentes &gt; Información</h1>
            </div>

            {/* Toolbar */}
            {/* <div className="mb-4 flex flex-wrap gap-3 items-center">
                <button onClick={handleNew} className="flex items-center gap-2 px-6 py-2 bg-blue-900 text-white rounded-full hover:bg-blue-800">
                    <Plus size={18} />
                    <span>Añadir Docente</span>
                </button>
                <div className="flex-1 max-w-xs relative">
                    <input type="text" placeholder="Buscar..." value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-4 pr-10 py-2 border border-gray-300 rounded-full text-sm" />
                    <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                </div>
                <button onClick={exportToExcel} className="p-2 bg-green-600 text-white rounded-full hover:bg-green-700 transition shadow-sm" title="Descargar Excel">
                    <Download size={20} />
                </button>
            </div> */}
            <div className="mb-6 bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                <div className="flex flex-wrap gap-4 items-end">


                    <div className="flex-grow flex items-end gap-2">
                        <div className="relative flex-grow">
                            <label className="block text-xs font-semibold text-gray-500 mb-1 ml-1">BUSCAR</label>
                            <div className="relative">
                                <Search className="absolute left-3 top-2.5 text-gray-400" size={16} />
                                <input
                                    type="text"
                                    placeholder="Buscar..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 hover:border-blue-500"
                                />
                            </div>
                        </div>
                        <button onClick={handleNew} className="flex items-center gap-2 px-6 py-2 bg-blue-900 text-white rounded-full hover:bg-blue-800">
                            <Plus size={18} />
                            <span>Añadir Docente</span>
                        </button>
                        <button onClick={exportToExcel} className="p-2 bg-green-600 text-white rounded-full hover:bg-green-700 transition shadow-sm" title="Descargar Excel">
                            <Download size={20} />
                        </button>

                    </div>
                </div>
            </div>

            {/* Tabla simplificada */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-gray-100">
                            <tr>
                                <th className="px-3 py-3 text-center font-semibold border">No</th>
                                <th className="px-3 py-3 text-center font-semibold border">Nombres</th>
                                <th className="px-3 py-3 text-center font-semibold border">Apellidos</th>
                                <th className="px-3 py-3 text-center font-semibold border">Cédula de Identidad</th>
                                <th className="px-3 py-3 text-center font-semibold border">Número de Teléfono/ Celular</th>
                                <th className="px-3 py-3 text-center font-semibold border">Correo Electrónico</th>
                                <th className="px-3 py-3 text-center font-semibold border">Activo/ Inactivo</th>
                                <th className="px-3 py-3 text-center font-semibold border">Acciones</th>
                            </tr>
                        </thead>
                        <tbody >
                            {filteredDocentes.length === 0 ? (
                                <tr><td colSpan="8" className="hover:bg-gray-50">No hay docentes registrados</td></tr>
                            ) : (
                                filteredDocentes.map((d, index) => (
                                    <tr key={d.id_docente} className="hover:bg-gray-50">
                                        <td className="px-3 py-2 text-center border">{index + 1}</td>
                                        <td className="px-3 py-3 border">{d.nombres || '-'}</td>
                                        <td className="px-3 py-3 border">{`${d.apellidos || ''} ${d.segundo_apellido || ''}`.trim() || '-'}</td>
                                        <td className="px-3 py-3 border">{d.cedula || '-'}</td>
                                        <td className="px-3 py-3 border" border>{d.telefono || d.celular || '-'}</td>
                                        <td className="px-3 py-3 border">{d.email || '-'}</td>
                                        <td className="px-3 py-3 text-center border">
                                            <span className={`px-2 py-1 rounded-full text-xs font-medium  ${d.activo ? 'bg-green-200 text-green-700' : 'bg-red-200 text-red-700'}`}>
                                                {d.activo ? 'Activo' : 'Inactivo'}
                                            </span>
                                        </td>
                                        <td className="px-3 py-3">
                                            <div className="flex justify-center gap-2">
                                                <button onClick={() => handleView(d)}
                                                    className="p-1.5 rounded-full transition shadow-sm bg-orange-100 text-orange-600 hover:bg-orange-200" title="Ver detalles">
                                                    <Eye size={16} />
                                                </button>
                                                <button onClick={() => handleEdit(d)}
                                                    className="p-1.5 rounded-full transition shadow-sm bg-blue-100 text-blue-600 hover:bg-blue-200"
                                                    title="Editar"
                                                >
                                                    <Edit size={14} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Modal Ver Detalles */}
            {showViewModal && selectedDocente && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
                        <div className="flex justify-between items-center px-6 py-4 border-b sticky top-0 bg-white z-10">
                            <h2 className="text-xl font-bold text-blue-900">Detalles del Docente</h2>
                            <button onClick={() => setShowViewModal(false)} className="text-gray-400 hover:text-gray-600">
                                <X size={24} />
                            </button>
                        </div>
                        <div className="p-6">
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                <div>
                                    <label className="block text-sm text-gray-500">Nombres</label>
                                    <p className="font-medium">{selectedDocente.nombres || '-'}</p>
                                </div>
                                <div>
                                    <label className="block text-sm text-gray-500">Primer Apellido</label>
                                    <p className="font-medium">{selectedDocente.apellidos || '-'}</p>
                                </div>
                                <div>
                                    <label className="block text-sm text-gray-500">Segundo Apellido</label>
                                    <p className="font-medium">{selectedDocente.segundo_apellido || '-'}</p>
                                </div>
                                <div>
                                    <label className="block text-sm text-gray-500">Cédula de Identidad</label>
                                    <p className="font-medium">{selectedDocente.cedula || '-'}</p>
                                </div>
                                <div>
                                    <label className="block text-sm text-gray-500">Extensión</label>
                                    <p className="font-medium">{selectedDocente.extension || '-'}</p>
                                </div>
                                <div>
                                    <label className="block text-sm text-gray-500">Complemento</label>
                                    <p className="font-medium">{selectedDocente.complemento || '-'}</p>
                                </div>
                                <div>
                                    <label className="block text-sm text-gray-500">Fecha de Nacimiento</label>
                                    <p className="font-medium">{selectedDocente.fecha_nacimiento ? new Date(selectedDocente.fecha_nacimiento).toLocaleDateString() : '-'}</p>
                                </div>
                                <div>
                                    <label className="block text-sm text-gray-500">Sexo</label>
                                    <p className="font-medium">{selectedDocente.sexo === 'M' ? 'Masculino' : selectedDocente.sexo === 'F' ? 'Femenino' : '-'}</p>
                                </div>
                                <div>
                                    <label className="block text-sm text-gray-500">Teléfono/Celular</label>
                                    <p className="font-medium">{selectedDocente.telefono || selectedDocente.celular || '-'}</p>
                                </div>
                                <div className="col-span-2">
                                    <label className="block text-sm text-gray-500">Correo Electrónico</label>
                                    <p className="font-medium">{selectedDocente.email || '-'}</p>
                                </div>
                                <div>
                                    <label className="block text-sm text-gray-500">Discapacidad</label>
                                    <p className="font-medium">{selectedDocente.discapacidad || '-'}</p>
                                </div>
                                <div>
                                    <label className="block text-sm text-gray-500">Especialidad</label>
                                    <p className="font-medium">{selectedDocente.especialidad || '-'}</p>
                                </div>
                                <div>
                                    <label className="block text-sm text-gray-500">Nivel de Formación</label>
                                    <p className="font-medium">{selectedDocente.nivel_formacion || '-'}</p>
                                </div>
                                <div>
                                    <label className="block text-sm text-gray-500">Descripción</label>
                                    <p className="font-medium">{selectedDocente.descripcion || '-'}</p>
                                </div>
                                <div>
                                    <label className="block text-sm text-gray-500">Otra Formación</label>
                                    <p className="font-medium">{selectedDocente.otra_formacion || '-'}</p>
                                </div>
                                <div>
                                    <label className="block text-sm text-gray-500">Estado</label>
                                    <p className="font-medium">
                                        <span className={`px-2 py-1 rounded-full text-xs ${selectedDocente.activo ? 'bg-green-200 text-green-800' : 'bg-red-200 text-red-800'}`}>
                                            {selectedDocente.activo ? 'Activo' : 'Inactivo'}
                                        </span>
                                    </p>
                                </div>
                                <div>
                                    <label className="block text-sm text-gray-500">Fecha Alta</label>
                                    <p className="font-medium">{selectedDocente.fecha_alta ? new Date(selectedDocente.fecha_alta).toLocaleDateString() : '-'}</p>
                                </div>
                                <div>
                                    <label className="block text-sm text-gray-500">Fecha Baja</label>
                                    <p className="font-medium">{selectedDocente.fecha_baja ? new Date(selectedDocente.fecha_baja).toLocaleDateString() : '-'}</p>
                                </div>
                                <div>
                                    <label className="block text-sm text-gray-500">CITE Memorándum Alta</label>
                                    <p className="font-medium">{selectedDocente.cite_memorandum_alta || '-'}</p>
                                </div>
                                <div>
                                    <label className="block text-sm text-gray-500">CITE Memorándum Baja</label>
                                    <p className="font-medium">{selectedDocente.cite_memorandum_baja || '-'}</p>
                                </div>
                                <div className="col-span-2 md:col-span-3">
                                    <label className="block text-sm text-gray-500">Observaciones</label>
                                    <p className="font-medium">{selectedDocente.observaciones || '-'}</p>
                                </div>
                            </div>
                        </div>
                        <div className="flex justify-end px-6 py-4 border-t">
                            <button onClick={() => setShowViewModal(false)} className="px-6 py-2 bg-blue-900 text-white rounded-md hover:bg-blue-800">
                                Cerrar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal Añadir/Editar */}
            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
                        <div className="flex justify-between items-center px-6 py-4 border-b sticky top-0 bg-white z-10">
                            <h2 className="text-xl font-bold text-blue-900">{editingId ? 'Editar Docente' : 'Añadir Docente'}</h2>
                            <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                                <X size={24} />
                            </button>
                        </div>
                        <div className="p-6">
                            {/* Row 1 */}
                            <div className="grid grid-cols-3 gap-4 mb-4">
                                <div>
                                    <label className="block text-sm font-medium mb-1">Nombres*</label>
                                    <input type="text" name="nombres" value={formData.nombres} onChange={handleInputChange}
                                        placeholder="Solo letras y espacios"
                                        className={`w-full px-3 py-2 border rounded-md ${errors.nombres ? 'border-red-500' : 'border-gray-300'}`} />
                                    {errors.nombres && <p className="text-red-500 text-xs mt-1">{errors.nombres}</p>}
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">Primer Apellido*</label>
                                    <input type="text" name="primer_apellido" value={formData.primer_apellido} onChange={handleInputChange}
                                        placeholder="Solo letras y espacios"
                                        className={`w-full px-3 py-2 border rounded-md ${errors.primer_apellido ? 'border-red-500' : 'border-gray-300'}`} />
                                    {errors.primer_apellido && <p className="text-red-500 text-xs mt-1">{errors.primer_apellido}</p>}
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">Segundo Apellido</label>
                                    <input type="text" name="segundo_apellido" value={formData.segundo_apellido} onChange={handleInputChange}
                                        placeholder="Solo letras y espacios"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">Usuario{editingId ? '' : '*'}</label>
                                    <input type="text" name="usuario" value={formData.usuario} onChange={handleInputChange}
                                        placeholder="ej: mperez"
                                        className={`w-full px-3 py-2 border rounded-md ${errors.usuario ? 'border-red-500' : 'border-gray-300'}`} />
                                    {errors.usuario && <p className="text-red-500 text-xs mt-1">{errors.usuario}</p>}
                                </div>
                                <div className="relative">
                                    <label className="block text-sm font-medium mb-1">Contraseña{editingId ? '' : '*'}</label>
                                    <div className="relative">
                                        <input
                                            type={showPassword ? "text" : "password"}
                                            name="password"
                                            value={formData.password}
                                            onChange={handleInputChange}
                                            placeholder={editingId ? "Dejar vacío si no modifica" : "Mínimo 8 caracteres"}
                                            className={`w-full px-3 py-2 border rounded-md pr-10 ${errors.password ? 'border-red-500' : 'border-gray-300'}`}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
                                        >
                                            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                        </button>
                                    </div>
                                    {errors.password && <p className="text-red-500 text-xs mt-1">{errors.password}</p>}
                                </div>
                            </div>

                            {/* Row 2 */}
                            <div className="grid grid-cols-6 gap-3 mb-4">
                                <div className="col-span-2">
                                    <label className="block text-sm font-medium mb-1">Cédula de Identidad*</label>
                                    <input type="text" name="cedula" value={formData.cedula} onChange={handleInputChange}
                                        placeholder="Solo números" maxLength={10}
                                        className={`w-full px-3 py-2 border rounded-md ${errors.cedula ? 'border-red-500' : 'border-gray-300'}`} />
                                    {errors.cedula && <p className="text-red-500 text-xs mt-1">{errors.cedula}</p>}
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">Extensión*</label>
                                    <select name="extension" value={formData.extension} onChange={handleInputChange}
                                        className={`w-full px-3 py-2 border rounded-md ${errors.extension ? 'border-red-500' : 'border-gray-300'}`}>
                                        <option value="">Seleccionar</option>
                                        {extensiones.map(e => <option key={e} value={e}>{e}</option>)}
                                    </select>
                                    {errors.extension && <p className="text-red-500 text-xs mt-1">{errors.extension}</p>}
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">Complemento</label>
                                    <input type="text" name="complemento" value={formData.complemento} onChange={handleInputChange}
                                        placeholder="Ej: 1A" maxLength={5}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">Fecha Nacimiento*</label>
                                    <input type="date" name="fecha_nacimiento" value={formData.fecha_nacimiento} onChange={handleInputChange}
                                        className={`w-full px-3 py-2 border rounded-md ${errors.fecha_nacimiento ? 'border-red-500' : 'border-gray-300'}`} />
                                    {errors.fecha_nacimiento && <p className="text-red-500 text-xs mt-1">{errors.fecha_nacimiento}</p>}
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">Sexo*</label>
                                    <select name="sexo" value={formData.sexo} onChange={handleInputChange}
                                        className={`w-full px-3 py-2 border rounded-md ${errors.sexo ? 'border-red-500' : 'border-gray-300'}`}>
                                        <option value="">Seleccionar</option>
                                        <option value="M">Masculino</option>
                                        <option value="F">Femenino</option>
                                    </select>
                                    {errors.sexo && <p className="text-red-500 text-xs mt-1">{errors.sexo}</p>}
                                </div>
                            </div>

                            {/* Row 3 */}
                            <div className="grid grid-cols-3 gap-4 mb-4">
                                <div>
                                    <label className="block text-sm font-medium mb-1">Número de Teléfono/Celular*</label>
                                    <input type="text" name="telefono" value={formData.telefono} onChange={handleInputChange}
                                        placeholder="Solo números (7-8 dígitos)" maxLength={8}
                                        className={`w-full px-3 py-2 border rounded-md ${errors.telefono ? 'border-red-500' : 'border-gray-300'}`} />
                                    {errors.telefono && <p className="text-red-500 text-xs mt-1">{errors.telefono}</p>}
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">Correo Electrónico*</label>
                                    <input type="email" name="email" value={formData.email} onChange={handleInputChange}
                                        placeholder="correo@ejemplo.com"
                                        className={`w-full px-3 py-2 border rounded-md ${errors.email ? 'border-red-500' : 'border-gray-300'}`} />
                                    {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">Discapacidad</label>
                                    <select name="discapacidad" value={formData.discapacidad} onChange={handleInputChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md">
                                        <option value="">Seleccionar</option>
                                        {discapacidades.map(d => <option key={d} value={d}>{d}</option>)}
                                    </select>
                                </div>
                            </div>

                            {/* Row 4 */}
                            <div className="grid grid-cols-3 gap-4 mb-4">
                                <div>
                                    <label className="block text-sm font-medium mb-1">Especialidades*</label>
                                    <input type="text" name="especialidad" value={formData.especialidad} onChange={handleInputChange}
                                        placeholder="Ingrese especialidades"
                                        className={`w-full px-3 py-2 border rounded-md ${errors.especialidad ? 'border-red-500' : 'border-gray-300'}`} />
                                    {errors.especialidad && <p className="text-red-500 text-xs mt-1">{errors.especialidad}</p>}
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">Nivel de Formación*</label>
                                    <select name="nivel_formacion" value={formData.nivel_formacion} onChange={handleInputChange}
                                        className={`w-full px-3 py-2 border rounded-md ${errors.nivel_formacion ? 'border-red-500' : 'border-gray-300'}`}>
                                        <option value="">Seleccionar</option>
                                        {nivelesFormacion.map(n => <option key={n} value={n}>{n}</option>)}
                                    </select>
                                    {errors.nivel_formacion && <p className="text-red-500 text-xs mt-1">{errors.nivel_formacion}</p>}
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">Descripción</label>
                                    <input type="text" name="descripcion" value={formData.descripcion} onChange={handleInputChange}
                                        placeholder="Ingrese descripción"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md" />
                                </div>
                            </div>

                            {/* Row 5 */}
                            <div className="grid grid-cols-5 gap-3 mb-4">
                                <div>
                                    <label className="block text-sm font-medium mb-1">Otra Formación</label>
                                    <input type="text" name="otra_formacion" value={formData.otra_formacion} onChange={handleInputChange}
                                        placeholder="Otra formación"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md" />
                                </div>
                                <div className="flex items-end pb-2">
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input type="checkbox" name="activo" checked={formData.activo} onChange={handleInputChange} className="w-5 h-5" />
                                        <span className="text-sm">Activo/Inactivo</span>
                                    </label>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">Fecha Alta</label>
                                    <input type="date" name="fecha_alta" value={formData.fecha_alta} onChange={handleInputChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">Fecha Baja</label>
                                    <input type="date" name="fecha_baja" value={formData.fecha_baja} onChange={handleInputChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md" />
                                </div>
                                <div></div>
                            </div>

                            {/* Row 6 */}
                            <div className="grid grid-cols-2 gap-4 mb-4">
                                <div>
                                    <label className="block text-sm font-medium mb-1">CITE Memorándum Alta</label>
                                    <input type="text" name="cite_memorandum_alta" value={formData.cite_memorandum_alta} onChange={handleInputChange}
                                        placeholder="CITE-XXXX-XXX"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">CITE Memorándum Baja</label>
                                    <input type="text" name="cite_memorandum_baja" value={formData.cite_memorandum_baja} onChange={handleInputChange}
                                        placeholder="CITE-XXXX-XXX"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md" />
                                </div>
                            </div>

                            {/* Row 7 */}
                            <div className="mb-4">
                                <label className="block text-sm font-medium mb-1">Observaciones</label>
                                <textarea name="observaciones" value={formData.observaciones} onChange={handleInputChange}
                                    placeholder="Ingrese observaciones..."
                                    rows={3} className="w-full px-3 py-2 border border-gray-300 rounded-md" />
                            </div>
                        </div>
                        <div className="flex justify-end px-6 py-4 border-t">
                            <button onClick={handleSubmit} className="px-6 py-2 bg-blue-900 text-white rounded-md hover:bg-blue-800">
                                Guardar
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
